"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowRight, Cpu, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function OlderParts() {
  const containerRef = useRef<HTMLElement | null>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".reveal-item", {
        y: 12
      });
      gsap.to(".reveal-item", {
        opacity: 1,
        y: 0,
        duration: 0.3,
        stagger: 0.06,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".reveal-item", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") {
      refresh();
    } else {
      window.addEventListener("load", refresh, {
        once: true
      });
    }
    document.fonts?.ready?.then(refresh);
  }, {
    scope: containerRef
  });
  return <section ref={containerRef} id="findingpartsolderbulls" data-nav="light" className="w-full border-y border-border bg-background py-16 md:py-24 text-foreground">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Technical Header Block */}
        <div className="reveal-item border-l-2 border-primary pl-4 md:pl-6" style={{
        opacity: 0
      }}>
          <div className="flex items-center gap-2 text-xs font-mono font-medium uppercase tracking-wider text-muted-foreground">
            <Wrench className="size-3.5 text-primary" />
            <span>Technical Advisory &bull; Legacy Systems</span>
          </div>
          <h2 className="mt-2 text-3xl font-bold uppercase tracking-tight font-serif sm:text-4xl lg:text-5xl text-foreground">
            Finding Parts for Older Mechanical Bulls
          </h2>
        </div>

        {/* Informational Body Block */}
        <div className="mt-8 grid grid-cols-1 gap-6 text-base leading-relaxed text-muted-foreground font-sans md:text-lg">
          <div className="reveal-item space-y-6" style={{
          opacity: 0
        }}>
            <p className="border-b border-border/60 pb-6 text-foreground/90 font-medium">
              Older mechanical bulls can require more careful parts research because controllers, switches and other components may have changed over time. A discontinued component does not automatically mean the entire machine needs replacement, but it does make identification more important.
            </p>
            <p>
              Start with the exact part already installed. Photos, labels, serial information and connector details can often provide more useful clues than the age of the bull alone.
            </p>
            <p>
              If an exact replacement is no longer available, do not assume another component can be substituted without confirming how it interacts with the rest of the system.
            </p>
          </div>
        </div>

        {/* Industrial Rating Plate Callout Banner */}
        <div className="reveal-item mt-10 rounded-none border border-border bg-muted/60 p-6 md:p-8 shadow-xs" style={{
        opacity: 0
      }}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="mt-1 flex size-7 shrink-0 items-center justify-center border border-border bg-background text-primary">
                <Cpu className="size-4" />
              </div>
              <p className="text-sm md:text-base font-sans font-medium text-foreground">
                Buying an older machine?{" "}
                <Link to="/UsedMechanicalBullsPage" className="inline-flex items-center gap-1 font-semibold text-primary underline underline-offset-4 decoration-primary/60 hover:decoration-primary transition-colors">
                  Used Mechanical Bulls
                  <ArrowRight className="size-4 inline-block" />
                </Link>{" "}
                page before comparing parts requirements.
              </p>
            </div>
            <div className="shrink-0 pt-2 sm:pt-0">
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground border border-border px-2.5 py-1 bg-background">
                REF-ID: UMB-GUIDE
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}